import React from "react";
import { useLocation } from "react-router-dom";
import { logClientError } from "../utils/logShipper";
import Button from "./Button";

type ErrorBoundaryVariant = "page" | "widget";

interface ErrorBoundaryProps {
    children: React.ReactNode;
    /**
     * "page" fills the content area and offers Reload / Go home.
     * "widget" degrades to a compact inline card so one broken widget doesn't
     * take down the rest of the screen.
     */
    variant?: ErrorBoundaryVariant;
    /** Human name of what failed ("Missions", "finance tab") — shipped with the error and shown to the user. */
    label?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Catches render errors in its subtree, reports them to the backend via the
 * client log shipper (System → Web Error Logs), and shows a recoverable
 * fallback instead of a blank page.
 *
 * Mount these narrowly. A single boundary at the app root — which is all this
 * app had — means any one bad component blanks the entire application, chrome
 * and all, with no way to navigate away. Per-route boundaries live in
 * `withDefaultLayout`; the dashboard wraps each tab.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error("React render error:", error, info);
        logClientError(error, {
            componentStack: info.componentStack ?? undefined,
            label: this.props.label,
            source: "ErrorBoundary",
        });
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    handleReload = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    handleGoHome = () => {
        this.setState({ hasError: false, error: null });
        window.location.href = "/";
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const { variant = "page", label } = this.props;
        const what = label ? `"${label}"` : "This section";

        if (variant === "widget") {
            return (
                <div style={{
                    border: "1px solid rgba(220,53,69,0.25)",
                    background: "rgba(220,53,69,0.05)",
                    borderRadius: 8,
                    padding: 16,
                    margin: 8,
                }}>
                    <div style={{ marginBottom: 8 }}>
                        <i className="bi bi-exclamation-triangle me-2" />
                        {what} failed to load. The rest of the page is unaffected, and the
                        error has been reported.
                    </div>
                    {this.state.error?.message && (
                        <pre style={{
                            background: "rgba(0,0,0,0.04)",
                            padding: 8,
                            borderRadius: 4,
                            fontSize: "var(--fs-2xs)",
                            overflow: "auto",
                            maxHeight: 120,
                            marginBottom: 8,
                        }}>{this.state.error.message}</pre>
                    )}
                    <Button onClick={this.handleRetry} icon="arrow-clockwise">
                        Try again
                    </Button>
                </div>
            );
        }

        return (
            <div style={{
                padding: "40px 24px",
                maxWidth: 720,
                margin: "60px auto",
                fontFamily: "system-ui, -apple-system, sans-serif",
            }}>
                <div style={{
                    border: "1px solid rgba(220,53,69,0.25)",
                    background: "rgba(220,53,69,0.05)",
                    borderRadius: 8,
                    padding: 24,
                }}>
                    <h2 style={{ marginTop: 0, color: "var(--status-danger-solid)" }}>
                        <i className="bi bi-exclamation-triangle me-2" />
                        Something went wrong
                    </h2>
                    <p style={{ marginBottom: 16 }}>
                        {what} hit an unexpected error. The details have been reported automatically —
                        try again, or head back and carry on elsewhere.
                    </p>
                    {this.state.error?.message && (
                        <pre style={{
                            background: "rgba(0,0,0,0.04)",
                            padding: 12,
                            borderRadius: 4,
                            fontSize: "var(--fs-xs)",
                            overflow: "auto",
                            maxHeight: 240,
                        }}>{this.state.error.message}</pre>
                    )}
                    <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                        <Button onClick={this.handleRetry} icon="arrow-clockwise">
                            Try again
                        </Button>
                        <Button onClick={this.handleReload} icon="arrow-repeat">
                            Reload page
                        </Button>
                        <Button onClick={this.handleGoHome} variant="ghost" icon="house">
                            Go home
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

/**
 * Per-route boundary. Keyed on the pathname so that navigating away from a
 * crashed page mounts a fresh boundary — otherwise the error state would
 * persist and the next page would render the fallback too.
 */
export const RouteErrorBoundary: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label }) => {
    const location = useLocation();
    return (
        <ErrorBoundary key={location.pathname} variant="page" label={label}>
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundary;
